import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { buildBrandScopedPath } from "../../lib/brand-navigation";
import { Button } from "../ui/Button";

type CorpusSummary = { id: string; title?: string; source: string; created_at: string };
type CorpusEntry = CorpusSummary & { brand_id: string; content: string; metadata?: Record<string, unknown> };
type Claim = { text: string; sourceId: string };
type ReadCorpus = (id: string) => Promise<CorpusEntry>;

export async function sha256Utf8(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function verifyBlogSource(
  brandId: string,
  id: string,
  read: ReadCorpus = async (sourceId) => {
    const response = await api.get<{ data: CorpusEntry }>(`/api/v1/brands/${encodeURIComponent(brandId)}/corpus/${encodeURIComponent(sourceId)}`);
    return response.data;
  },
): Promise<{ entry: CorpusEntry; digest: string }> {
  const entry = await read(id);
  if (entry.id !== id || entry.brand_id !== brandId || !Number.isFinite(Date.parse(entry.created_at))
    || typeof entry.content !== "string" || entry.content.trim().length < 80 || entry.content.length > 16_000) {
    throw new Error("A selected source is unavailable or outside the accepted length. Refresh the source list.");
  }
  return { entry, digest: await sha256Utf8(entry.content) };
}

export async function resolveSelectedBlogSources(
  brandId: string,
  selectedIds: string[],
  read?: ReadCorpus,
): Promise<Record<string, string>> {
  if (selectedIds.length < 1 || selectedIds.length > 5 || new Set(selectedIds).size !== selectedIds.length) {
    throw new Error("Choose one to five sources.");
  }
  const digests: Record<string, string> = {};
  let totalLength = 0;
  for (const id of selectedIds) {
    const { entry, digest } = await verifyBlogSource(brandId, id, read);
    totalLength += entry.content.length;
    digests[id] = digest;
  }
  if (totalLength > 30_000) throw new Error("Selected sources exceed 30,000 characters. Choose a smaller packet.");
  return digests;
}

function required(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`${label} is required.`);
  return trimmed;
}

export function BlogComposeLaunch({ brandId, pending, onSubmit }: {
  brandId: string;
  pending: boolean;
  onSubmit: (params: Record<string, unknown>) => Promise<void>;
}) {
  const [sources, setSources] = useState<CorpusSummary[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [claims, setClaims] = useState<Claim[]>([{ text: "", sourceId: "" }]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lookupId, setLookupId] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setSources([]);
    setSelected([]);
    setLoadError(null);
    void api.get<{ data: { entries: CorpusSummary[] } }>(`/api/v1/brands/${encodeURIComponent(brandId)}/corpus?limit=200`)
      .then((response) => { if (active) setSources(response.data.entries.filter((entry) => entry.id && entry.created_at)); })
      .catch((cause) => { if (active) setLoadError(cause instanceof Error ? cause.message : "Unable to load brand sources."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [brandId]);

  function toggleSource(id: string) {
    setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : current.length < 5 ? [...current, id] : current);
  }

  async function addSourceById() {
    const id = lookupId.trim();
    if (!id || lookupBusy || submitting || pending) return;
    setError(null);
    if (sources.some((source) => source.id === id)) {
      setError("That source is already listed. Select it above.");
      return;
    }
    if (selected.length >= 5) {
      setError("Choose no more than five sources.");
      return;
    }
    setLookupBusy(true);
    try {
      const { entry } = await verifyBlogSource(brandId, id);
      setSources((current) => [...current, { id: entry.id, title: typeof entry.metadata?.title === "string" ? entry.metadata.title : undefined, source: entry.source, created_at: entry.created_at }]);
      setSelected((current) => [...current, id]);
      setLookupId("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to find an owned source with that ID.");
    } finally {
      setLookupBusy(false);
    }
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending || submitting || loading || lookupBusy) return;
    setError(null);
    setSubmitting(true);
    try {
      const form = new FormData(event.currentTarget);
      if (!selected.length) throw new Error("Choose at least one brand source before starting.");
      const title = required(String(form.get("title") ?? ""), "Working title");
      const targetReader = required(String(form.get("target_reader") ?? ""), "Target reader");
      const readerProblem = required(String(form.get("reader_problem") ?? ""), "Reader problem");
      const takeaway = required(String(form.get("intended_takeaway") ?? ""), "Intended takeaway");
      const searchIntent = required(String(form.get("search_intent_hypothesis") ?? ""), "Search intent hypothesis");
      const callToAction = required(String(form.get("call_to_action") ?? ""), "Call to action");
      const minWords = Number(form.get("min_words"));
      const maxWords = Number(form.get("max_words"));
      if (!Number.isInteger(minWords) || !Number.isInteger(maxWords) || minWords < 200 || maxWords > 2500
        || maxWords < minWords || maxWords - minWords > 1000) throw new Error("Choose a range of 200–2,500 words, at most 1,000 words wide.");
      const claimMappings = claims.map((claim) => ({ claim: claim.text.trim(), source_ids: [claim.sourceId] }));
      if (claimMappings.some((claim) => !claim.claim || !claim.source_ids[0] || !selected.includes(claim.source_ids[0]))) throw new Error("Give each claim a selected source.");
      const requiredLinks = String(form.get("required_links") ?? "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
      if (requiredLinks.length > 8 || requiredLinks.some((link) => {
        try { const url = new URL(link); return url.protocol !== "https:" || Boolean(url.username || url.password); } catch { return true; }
      })) throw new Error("Required links must be HTTPS URLs, up to eight, one per line.");
      const sourceDigests = await resolveSelectedBlogSources(brandId, selected);
      await onSubmit({ brief: {
        version: "blog-brief-v1", title, target_reader: targetReader, reader_problem: readerProblem,
        intended_takeaway: takeaway, search_intent_hypothesis: searchIntent,
        min_words: minWords, max_words: maxWords, source_ids: selected, source_digests: sourceDigests,
        claim_mappings: claimMappings, required_links: requiredLinks, call_to_action: callToAction,
      } });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to start the blog Action.");
    } finally {
      setSubmitting(false);
    }
  }

  return <form className="mt-6 space-y-5 rounded-2xl border border-slate-200 bg-white p-6" onSubmit={(event) => void submit(event)}>
    <p className="text-sm leading-6 text-slate-600">Choose saved brand sources, then describe the reader and claims. Passing drafts go to Review for your decision; this Action does not publish.</p>
    <fieldset className="space-y-2" disabled={pending || submitting || loading}>
      <legend className="text-sm font-semibold text-slate-900">Sources (1–5)</legend>
      {loading ? <p className="text-sm text-slate-500">Loading brand sources…</p> : null}
      {loadError ? <p role="alert" className="text-sm text-red-700">{loadError}</p> : null}
      {!loading && !loadError && sources.length === 0 ? <p className="text-sm text-amber-800">No saved sources are available. <Link className="underline" to={buildBrandScopedPath("/corpus", brandId)}>Add a source to Corpus</Link> first.</p> : null}
      {sources.map((source) => <label key={source.id} className="flex items-start gap-3 rounded-xl border border-slate-200 p-3 text-sm">
        <input type="checkbox" checked={selected.includes(source.id)} onChange={() => toggleSource(source.id)} className="mt-1" />
        <span><strong className="block text-slate-900">{source.title || source.id}</strong><span className="text-slate-500">{source.source} · Added to Corpus {new Date(source.created_at).toLocaleDateString()}</span></span>
      </label>)}
      {sources.length > 0 ? <p className="text-xs text-slate-500">{selected.length} selected. Each source is checked again when you start.</p> : null}
    </fieldset>
    <div className="space-y-2">
      <label htmlFor="blog-source-id" className="block text-sm font-medium text-slate-800">Add a saved source by ID</label>
      <p className="text-xs text-slate-500">The list shows up to 200 recent Corpus entries. Use an ID to select another source owned by this brand.</p>
      <div className="flex flex-wrap gap-2"><input id="blog-source-id" value={lookupId} onChange={(event) => setLookupId(event.target.value)} disabled={pending || submitting || lookupBusy} className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm" /><Button type="button" variant="secondary" disabled={!lookupId.trim() || pending || submitting || lookupBusy} onClick={() => void addSourceById()}>{lookupBusy ? "Checking…" : "Add source"}</Button></div>
    </div>
    <div className="grid gap-4 sm:grid-cols-2">
      {([
        ["title", "Working title", 180], ["target_reader", "Target reader", 500],
        ["reader_problem", "Reader problem", 500], ["intended_takeaway", "Intended takeaway", 500],
        ["search_intent_hypothesis", "Search intent hypothesis", 500], ["call_to_action", "Call to action", 500],
      ] as const).map(([name, label, maxLength]) => <label key={name} className="block text-sm font-medium text-slate-800">{label}
        <input required maxLength={maxLength} name={name} disabled={pending || submitting} className="mt-1 block w-full rounded-xl border border-slate-300 px-3 py-2 font-normal" />
      </label>)}
      <label className="text-sm font-medium text-slate-800">Minimum words<input required type="number" min={200} max={2500} defaultValue={600} name="min_words" disabled={pending || submitting} className="mt-1 block w-full rounded-xl border border-slate-300 px-3 py-2" /></label>
      <label className="text-sm font-medium text-slate-800">Maximum words<input required type="number" min={200} max={2500} defaultValue={900} name="max_words" disabled={pending || submitting} className="mt-1 block w-full rounded-xl border border-slate-300 px-3 py-2" /></label>
    </div>
    <fieldset className="space-y-3"><legend className="text-sm font-semibold text-slate-900">Claims to ground in the sources</legend>
      {claims.map((claim, index) => <div key={index} className="grid gap-2 sm:grid-cols-[1fr_12rem]">
        <input aria-label={`Claim ${index + 1}`} value={claim.text} maxLength={500} disabled={pending || submitting} onChange={(event) => setClaims((current) => current.map((item, i) => i === index ? { ...item, text: event.target.value } : item))} placeholder="Claim supported by the source" className="rounded-xl border border-slate-300 px-3 py-2 text-sm" />
        <select aria-label={`Source for claim ${index + 1}`} value={claim.sourceId} disabled={pending || submitting} onChange={(event) => setClaims((current) => current.map((item, i) => i === index ? { ...item, sourceId: event.target.value } : item))} className="rounded-xl border border-slate-300 px-3 py-2 text-sm"><option value="">Choose source</option>{sources.filter((source) => selected.includes(source.id)).map((source) => <option key={source.id} value={source.id}>{source.title || source.id}</option>)}</select>
      </div>)}
      <Button type="button" variant="secondary" disabled={claims.length >= 20 || pending || submitting} onClick={() => setClaims((current) => [...current, { text: "", sourceId: "" }])}>Add claim</Button>
    </fieldset>
    <label className="block text-sm font-medium text-slate-800">Required links (optional, one HTTPS URL per line)<textarea name="required_links" rows={3} disabled={pending || submitting} className="mt-1 block w-full rounded-xl border border-slate-300 px-3 py-2 font-normal" /></label>
    {error ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
    <Button type="submit" disabled={pending || submitting || loading || lookupBusy || sources.length === 0}>{pending || submitting ? "Starting…" : "Draft for Review"}</Button>
  </form>;
}
